import { config } from "../Environment";
import type { OutputEngagementRun } from "./programs";
import type { CustomThemeSpec } from "../components/pwf/journeyMap/themes";

// ── Core PWF Types ────────────────────────────────────────────────────────────

export type PwfType =
  | "pwf"
  | "activity"
  | "action_item"
  | "meeting"
  | "identity_space"
  | "program"
  | "trait";

// What level(s) of work a PWF node predominantly represents. Not exclusive —
// a node can carry more than one, e.g. mostly "execution" while also "planning".
export type PwfLevel = "vision" | "planning" | "execution";

export type LifecycleState =
  | "draft"
  | "pending"
  | "active"
  | "in_progress"
  | "completed"
  | "paused"
  | "archived";

export interface IoItem {
  key: string;
  type: string;
  required: boolean;
  description?: string;
}

export interface LinkedObject {
  objectType: string;
  objectId: string;
  relationship: string;
}

export interface AiContext {
  suggestions: AiSuggestion[] | null;
  lastSuggestedAt: string | null;
}

export interface PwfLogEntry {
  type: "system" | "human";
  event: string;
  message: string;
  timestamp: string;
  actor?: string | null;
  meta?: Record<string, any> | null;
}

export interface GuidedQuestion {
  key: string;
  label: string;
  type: "textbox" | "textarea" | "checkbox";
  required: boolean;
}

export interface ReportingPolicy {
  level: "none" | "optional" | "required";
  guidedQuestions: GuidedQuestion[];
  isFixed?: boolean;
}

export interface Manifestation {
  pwfType: PwfType;
  enabledAt: string;
  enabledBy: string | null;
  config: Record<string, any>;
  isActive: boolean;
}

export interface AiSuggestion {
  id?: string;
  recommendedType?: PwfType;
  suggestedTitle?: string;
  title?: string;
  rationale?: string;
  reasoning?: string;
  subItems?: Array<{ title: string; pwfType: PwfType; reasoning?: string }>;
  inputs?: IoItem[];
  suggestedKpi?: string;
  confidence?: number;
  nextSteps?: string[];
}

export interface PwfMeta {
  fiParent: string | null;
  ancestorIds: string[];
  children: string[];
  inputs: IoItem[];
  outputs: IoItem[];
  linkedObjects: LinkedObject[];
  aiContext: AiContext | null;
  levels: PwfLevel[];
}

export interface PwfCollaborator {
  _id: string;
  fiUser: string;
  invitedBy?: string | null;
  scopedToThisNode: boolean;
  joinedAt: string;
  /** Populated display name — may be present if backend populates */
  displayName?: string;
  firstName?: string;
  email?: string;
}

export interface PwfShareGrant {
  user: string;
  sharedBy: string;
}

export interface Pwf {
  _id: string;
  title: string;
  pwfType: PwfType;
  lifecycleState: LifecycleState;
  fiOwner: string | null;
  fiCoOwners?: string[];
  fiMentor?: string | null;
  pwfMeta: PwfMeta;
  config: Record<string, any>;
  manifestations: Manifestation[];
  isTemplate: boolean;
  sourceTemplateId?: string | null;
  templateSyncedAt?: string | null;
  isPrivacyFixed?: boolean;
  isPrivacyOptedOut?: boolean;
  usageLimit?: number | null;
  instantiationCount?: number;
  sharedWith?: PwfShareGrant[];
  sharedCollections?: string[];
  allowCollaboratorInvites?: boolean;
  collaborators?: PwfCollaborator[];
  userCanAddChildren?: boolean;
  userCanEdit?: boolean;
  supportedLanguages?: string[];
  defaultLanguage?: string;
  __isShared?: boolean;
  __isMentoring?: boolean;
  __existingInstanceId?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  activatedAt: string | null;
  completedAt: string | null;
  lastActivityAt: string | null;
  payload?: Record<string, any>;
  logs?: PwfLogEntry[];
}

export interface CreatePwfRequest {
  title: string;
  pwfType?: PwfType;
  lifecycleState?: LifecycleState;
  config?: Record<string, any>;
  supportedLanguages?: string[];
  defaultLanguage?: string;
}

export interface UpdatePwfRequest {
  title?: string;
  pwfType?: PwfType;
  lifecycleState?: LifecycleState;
  config?: Record<string, any>;
  isTemplate?: boolean;
  isPrivacyFixed?: boolean;
  isPrivacyOptedOut?: boolean;
  usageLimit?: number | null;
  allowCollaboratorInvites?: boolean;
  userCanAddChildren?: boolean;
  userCanEdit?: boolean;
  pwfMeta?: Partial<Pick<PwfMeta, "inputs" | "outputs" | "linkedObjects" | "aiContext" | "levels">>;
  supportedLanguages?: string[];
  defaultLanguage?: string;
}

// ── API Functions ─────────────────────────────────────────────────────────────

export const listPwfs = async (
  retryFetch: Function,
  params?: { pwfType?: PwfType; lifecycleState?: LifecycleState; isTemplate?: boolean }
): Promise<Pwf[]> => {
  const qs = new URLSearchParams();
  if (params?.pwfType) qs.set("pwfType", params.pwfType);
  if (params?.lifecycleState) qs.set("lifecycleState", params.lifecycleState);
  if (params?.isTemplate !== undefined) qs.set("isTemplate", String(params.isTemplate));
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await retryFetch(`${config.API_BASE}/api/pwf${query}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list PWFs: ${res.statusText}`);
  const data = await res.json();
  // Backend returns array directly or wrapped — handle both
  return Array.isArray(data) ? data : data.pwfs ?? [];
};

export const getPwf = async (
  retryFetch: Function,
  id: string,
  includeChildren = false
): Promise<{ pwf: Pwf; children: Pwf[] }> => {
  const qs = includeChildren ? "?includeChildren=true" : "";
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}${qs}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Failed to get PWF: ${res.statusText}`);
  }
  return res.json();
};

export const createPwf = async (
  retryFetch: Function,
  data: CreatePwfRequest
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create PWF: ${res.statusText}`);
  return res.json();
};

export const updatePwf = async (
  retryFetch: Function,
  id: string,
  data: UpdatePwfRequest
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update PWF: ${res.statusText}`);
  return res.json();
};

export const deletePwf = async (
  retryFetch: Function,
  id: string
): Promise<{ success: boolean }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to archive PWF: ${res.statusText}`);
  return res.json();
};

export const restorePwf = async (
  retryFetch: Function,
  id: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/restore`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to restore PWF: ${res.statusText}`);
  return res.json();
};

export const getPwfChildren = async (
  retryFetch: Function,
  id: string
): Promise<{ children: Pwf[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/children`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to get children: ${res.statusText}`);
  return res.json();
};

export const getPwfDescendantTitles = async (
  retryFetch: Function,
  id: string
): Promise<{ _id: string; title: string; dimensionSetId: string | null }[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/descendant-titles`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load descendants: ${res.statusText}`);
  const body = await res.json();
  return body.descendants ?? [];
};

export const getPwfAncestorTitles = async (
  retryFetch: Function,
  id: string
): Promise<{ _id: string; title: string }[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/ancestor-titles`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load ancestors: ${res.statusText}`);
  const body = await res.json();
  return body.ancestors ?? [];
};

export const addPwfChild = async (
  retryFetch: Function,
  parentId: string,
  childId: string
): Promise<{ parent: Pwf; child: Pwf }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${parentId}/children`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ childId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to add child: ${res.statusText}`);
  }
  return res.json();
};

// Links an existing PWF as the "develop it myself" artifact for a gap on
// pwfId (the PWF with the gap) — a two-sided link, see linkGapResolutionPwf
// in pwfController.js. Returns the updated gapEntries map.
export const linkGapResolutionPwf = async (
  retryFetch: Function,
  pwfId: string,
  gapKey: string,
  targetPwfId: string
): Promise<{ gapEntries: Record<string, GapEntry> }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/link-gap-resolution`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ gapKey, targetPwfId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to link PWF: ${res.statusText}`);
  }
  return res.json();
};

// Creates a new action item (UserTakeaway) as the "develop it myself"
// artifact for a gap on pwfId — a two-sided link, see
// createGapResolutionActionItem in pwfController.js.
export const createGapResolutionActionItem = async (
  retryFetch: Function,
  pwfId: string,
  gapKey: string,
  data: { content: string; priority?: "high" | "medium" | "low"; dueDate?: number }
): Promise<{ gapEntries: Record<string, GapEntry>; takeawayId: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/gap-resolution-action-item`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ gapKey, ...data }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to create action item: ${res.statusText}`);
  }
  return res.json();
};

// Links an existing IdentitySpace as the "develop it myself" artifact for a
// gap on pwfId — a two-sided link, see linkGapResolutionIdentitySpace in
// pwfController.js.
export const linkGapResolutionIdentitySpace = async (
  retryFetch: Function,
  pwfId: string,
  gapKey: string,
  targetSpaceId: string
): Promise<{ gapEntries: Record<string, GapEntry> }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/link-gap-resolution-identity-space`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ gapKey, targetSpaceId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to link identity space: ${res.statusText}`);
  }
  return res.json();
};

// Links an existing Program (one this user created as mentor) as the
// "develop it myself" artifact for a gap on pwfId — a two-sided link, see
// linkGapResolutionProgram in pwfController.js.
export const linkGapResolutionProgram = async (
  retryFetch: Function,
  pwfId: string,
  gapKey: string,
  targetProgramId: string
): Promise<{ gapEntries: Record<string, GapEntry> }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/link-gap-resolution-program`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ gapKey, targetProgramId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to link program: ${res.statusText}`);
  }
  return res.json();
};

// Links an existing activity (Prescription) as the "develop it myself"
// artifact for a gap on pwfId — a two-sided link, see
// linkGapResolutionActivity in pwfController.js.
export const linkGapResolutionActivity = async (
  retryFetch: Function,
  pwfId: string,
  gapKey: string,
  targetPrescriptionId: string
): Promise<{ gapEntries: Record<string, GapEntry> }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/link-gap-resolution-activity`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ gapKey, targetPrescriptionId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to link activity: ${res.statusText}`);
  }
  return res.json();
};

export interface GapMentor {
  _id: string;
  displayName: string;
  email: string;
}

// The requester's active mentor(s) — a "Get mentorship" request can only be
// sent to one of these. Empty means no active mentor relationship exists.
export const listMyActiveMentors = async (
  retryFetch: Function
): Promise<GapMentor[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/gap-resolution-mentors`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load mentors");
  const data = await res.json();
  return data.mentors ?? [];
};

// Sends the chosen mentor a real notification asking for help with a
// specific gap on pwfId, and (server-side) writes resolutionStrategy:
// "mentorship" + linkedEntityId/linkedEntityName (the mentor) onto the
// GapEntry — the same "done" state shape every other functional resolution
// strategy uses. Caller relies on refetch to pick up the new gapEntries,
// same pattern as the Link*Dialog components.
export const requestGapMentorship = async (
  retryFetch: Function,
  pwfId: string,
  data: { gapKey: string; gapLabel: string; expertId: string; message?: string }
): Promise<void> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/request-gap-mentorship`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to request mentorship: ${res.statusText}`);
  }
};

export const reorderPwfChildren = async (
  retryFetch: Function,
  parentId: string,
  orderedIds: string[]
): Promise<Pwf> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/pwf/${parentId}/children/reorder`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ orderedIds }),
    }
  );
  if (!res.ok) throw new Error(`Failed to reorder children: ${res.statusText}`);
  return res.json();
};

export const activatePwf = async (
  retryFetch: Function,
  id: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/activate`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to activate PWF: ${res.statusText}`);
  return res.json();
};

export const completePwf = async (
  retryFetch: Function,
  id: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/complete`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to complete PWF: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  return res.json();
};

export const getManifestations = async (
  retryFetch: Function,
  id: string
): Promise<{ primary: { pwfType: PwfType; lifecycleState: string }; manifestations: Manifestation[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/manifestations`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to get manifestations: ${res.statusText}`);
  return res.json();
};

export const enableManifestation = async (
  retryFetch: Function,
  id: string,
  pwfType: PwfType,
  personalityConfig?: Record<string, any>
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/manifest/enable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ pwfType, config: personalityConfig }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to enable personality: ${res.statusText}`);
  }
  return res.json();
};

export const disableManifestation = async (
  retryFetch: Function,
  id: string,
  pwfType: PwfType
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/manifest/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ pwfType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to disable personality: ${res.statusText}`);
  }
  return res.json();
};

export const resolvePwfActivityLink = async (
  retryFetch: Function,
  pwfId: string
): Promise<{ prescriptionId: string | null; name?: string | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/activity-link`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to resolve activity link: ${res.statusText}`);
  return res.json();
};

export const getPwfActivityRuns = async (
  retryFetch: Function,
  pwfId: string
): Promise<{ engagementRuns: OutputEngagementRun[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/activity-runs`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch activity runs: ${res.statusText}`);
  return res.json();
};

export const addPwfLog = async (
  retryFetch: Function,
  id: string,
  message: string,
  options?: { event?: string; meta?: Record<string, any> }
): Promise<{ success: boolean; entry: PwfLogEntry }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message, event: options?.event, meta: options?.meta }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? "Failed to add log entry");
  }
  return res.json();
};

export const getEffectivePwfPolicy = async (
  retryFetch: Function,
  id: string
): Promise<{ policy: ReportingPolicy | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/effective-policy`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch policy: ${res.statusText}`);
  return res.json();
};

export const resolvePwfMeetingLink = async (
  retryFetch: Function,
  pwfId: string
): Promise<{ templateId: string | null; templateName: string | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/meeting-link`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to resolve meeting link: ${res.statusText}`);
  return res.json();
};

export const togglePwfStatus = async (
  retryFetch: Function,
  id: string
): Promise<{ _id: string; newStatus: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/toggle-status`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to toggle PWF status: ${res.statusText}`);
  return res.json();
};

export const requestPwfSuggestions = async (
  retryFetch: Function,
  id: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/suggest`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to generate suggestions: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  return res.json();
};

export const acceptPwfSuggestion = async (
  retryFetch: Function,
  id: string,
  suggestionIdx: number
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/suggest/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ suggestionIdx }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to accept suggestion: ${res.statusText}`);
  }
  return res.json();
};

export const dismissPwfSuggestion = async (
  retryFetch: Function,
  id: string,
  suggestionIdx: number
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${id}/suggest/dismiss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ suggestionIdx }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to dismiss suggestion: ${res.statusText}`);
  }
  return res.json();
};

// ── Studio: journey drafting ──────────────────────────────────────────────────

export interface JourneyInterview {
  audience: string;
  struggle?: string;
  transformation: string;
  approach?: string;
}

export type HabitCadence = "daily" | "weekly" | "in_the_moment";

export interface HabitDef {
  name: string;
  cadence: HabitCadence;
  /** Short reminder nudge the learner will receive */
  prompt: string;
  /** Habit continues into all later phases of the journey */
  carryForward: boolean;
  /** Practices required before the phase can complete (no gate when unset) */
  target?: number;
}

export interface DoItemDraft {
  title: string;
  kind: "action_item" | "activity" | "meeting";
}

export interface JourneyPhaseDraft {
  title: string;
  doSide: string;
  doItems?: DoItemDraft[];
  identity: string;
  traits: string[];
  /** Honest real-week estimate for the phase (1-6), from the AI draft */
  estWeeks?: number;
  habits: HabitDef[];
  checkIn: string;
}

// ── Facets: richer structure hanging off the DO and BECOME sides ──────────────
// Kept in sync with DO_FACET_KEYS / BECOME_FACET_KEYS in the backend
// (ClaryNext-BE/services/pwfAiService.js).

export type DoFacetKey =
  | "deliverables"
  | "goals"
  | "vision"
  | "plan"
  | "challenges"
  | "risks"
  | "opportunities"
  | "partners";

export type BecomeFacetKey = "attitude" | "knowledge" | "mentalModels" | "skills";

export type FacetKey = DoFacetKey | BecomeFacetKey;

export const DO_FACET_KEYS: DoFacetKey[] = [
  "deliverables",
  "goals",
  "vision",
  "plan",
  "challenges",
  "risks",
  "opportunities",
  "partners",
];

export const BECOME_FACET_KEYS: BecomeFacetKey[] = [
  "attitude",
  "knowledge",
  "mentalModels",
  "skills",
];

export const FACET_LABEL: Record<FacetKey, string> = {
  deliverables: "Deliverables",
  goals: "Goals",
  vision: "Vision",
  plan: "Plan",
  challenges: "Challenges & support",
  risks: "Risks",
  opportunities: "Opportunities",
  partners: "Partners",
  attitude: "Attitude",
  knowledge: "Knowledge",
  mentalModels: "Mental models",
  skills: "Skills",
};

export interface FacetItem {
  text: string;
  /** Where the item came from — wizard draft, AI suggestion, or the user */
  source?: "wizard" | "ai" | "user";
}

export type FacetMap = Partial<Record<FacetKey, FacetItem[]>>;

/** An idea collected during drafting or by the user that hasn't been placed yet */
export interface IdeaBankItem {
  text: string;
  side: "do" | "become";
  facet: FacetKey;
  /** Title of the drafted phase this idea best belongs to, if any */
  phaseTitle?: string | null;
  source?: "wizard" | "ai" | "user";
}

export interface JourneyDraft {
  title: string;
  summary: string;
  /** Journey-level DO — the overall external work of the journey */
  do?: string;
  /** Journey-level BECOME — the final identity the phases build toward */
  become?: { identity?: string; traits?: string[] };
  /** Ideas the interview surfaced that didn't land in a phase */
  ideaBank?: IdeaBankItem[];
  phases: JourneyPhaseDraft[];
}

export interface PhaseBecomeSuggestion {
  identity: string;
  traits: string[];
  habits: HabitDef[];
  checkIn: string;
}

/** Normalizes legacy string habits (early studio data) into HabitDef objects */
export const normalizeHabit = (h: string | HabitDef): HabitDef =>
  typeof h === "string"
    ? { name: h, cadence: "daily", prompt: "", carryForward: false }
    : {
        name: h.name ?? "",
        cadence: h.cadence ?? "daily",
        prompt: h.prompt ?? "",
        carryForward: Boolean(h.carryForward),
        target: Number(h.target) > 0 ? Number(h.target) : undefined,
      };

/** Shape stored in a journey root PWF's config.studio */
export interface StudioJourneyConfig {
  isJourney: true;
  audience?: string;
  struggle?: string;
  transformation?: string;
  approach?: string;
  summary?: string;
  /** Journey-level DO — the overall external work of the journey */
  do?: string;
  /** Journey-level BECOME — the final identity the phase identities build toward */
  become?: { identity?: string; traits?: string[] };
  doFacets?: FacetMap;
  becomeFacets?: FacetMap;
  /** Unplaced ideas collected during drafting — assignable to phases later */
  ideaBank?: IdeaBankItem[];
}

/** Shape stored in a phase PWF's config.studio */
export interface StudioPhaseConfig {
  isPhase: true;
  do?: string;
  identity?: string;
  traits?: string[];
  /** Real-week sizing carried from the draft (v3 §−2); display-only, no scheduler */
  estWeeks?: number;
  habits?: Array<HabitDef | string>;
  checkIn?: string;
  /** Reflections required (one counts per day) before the phase can complete */
  checkInTarget?: number;
  doFacets?: FacetMap;
  becomeFacets?: FacetMap;
}

export const draftJourneyFromInterview = async (
  retryFetch: Function,
  interview: JourneyInterview
): Promise<JourneyDraft> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/studio/draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(interview),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to draft journey: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  const data = await res.json();
  return data.draft;
};

export const suggestPhaseBecome = async (
  retryFetch: Function,
  input: {
    journeyTitle?: string;
    journeySummary?: string;
    phaseTitle: string;
    doSide?: string;
  }
): Promise<PhaseBecomeSuggestion> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/studio/suggest-become`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to suggest: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  const data = await res.json();
  return data.suggestion;
};

export interface NextPhaseSuggestion {
  title: string;
  doSide: string;
  reasoning: string;
}

/**
 * Suggests the NEXT 2-3 phases of a journey being edited in the Studio —
 * gradual, on demand. Titles + DO sides only; the author develops each
 * phase's BECOME side separately. Costs 1 AI credit (402 when out).
 */
export const suggestNextPhases = async (
  retryFetch: Function,
  input: {
    journeyTitle?: string;
    summary?: string;
    doSide?: string;
    become?: { identity?: string; traits?: string[] };
    existingPhases?: string[];
  }
): Promise<{ phases: NextPhaseSuggestion[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/studio/suggest-phases`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to suggest: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  const data = await res.json();
  return data.suggestion;
};

export const suggestFacetIdeas = async (
  retryFetch: Function,
  input: {
    facet: FacetKey;
    journeyTitle?: string;
    journeySummary?: string;
    audience?: string;
    struggle?: string;
    transformation?: string;
    approach?: string;
    /** Scope to one phase; omit for journey-level suggestions */
    phaseTitle?: string;
    /** Items already captured for this facet — won't be repeated */
    existing?: string[];
  }
): Promise<string[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/studio/suggest-facet`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to suggest: ${res.statusText}`);
    (e as any).status = res.status;
    throw e;
  }
  const data = await res.json();
  return data.ideas ?? [];
};

// ── Trait Assessment ──────────────────────────────────────────────────────────

export const assessTraitPwf = async (
  retryFetch: Function,
  pwfId: string,
  level: 0 | 1 | 2 | 3,
  note?: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/assess`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ level, note }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to assess trait: ${res.statusText}`);
  }
  return res.json();
};

// ── Journey Map (quest dashboard) ─────────────────────────────────────────────
// Aggregate read model for the learner's gamified journey visualization.
// Mirrors getQuestSummary / setQuestTheme in ClaryNext-BE/controllers/pwfController.js.

export type QuestThemePolicy = "locked" | "presets" | "free";
export type CelebrationIntensity = "calm" | "balanced" | "celebratory";

export interface QuestThemePalette {
  accent?: string;
}

export interface QuestTheme {
  policy: QuestThemePolicy;
  allowedPacks: string[];
  base: { pack: string; palette: QuestThemePalette | null };
  override: { pack: string; palette: QuestThemePalette | null } | null;
  resolved: {
    pack: string;
    palette: QuestThemePalette | null;
    phaseIcons: Record<string, string> | null;
    celebrationIntensity: CelebrationIntensity;
    /** Present when the learner generated an AI theme (pack === "custom") */
    custom?: CustomThemeSpec | null;
  };
}

export interface QuestItem {
  id: string;
  title: string;
  pwfType: PwfType;
  state: LifecycleState;
  isPhase: boolean;
  linkedPrescriptionId: string | null;
  /** AI summary of what the learner produced in the linked activity run */
  runSummary: string | null;
  /** ISO due date stamped when the item's phase engaged (one-task-store) */
  dueDate?: string | null;
}

export interface QuestPhaseHabit {
  name: string;
  cadence: HabitCadence;
  carryForward: boolean;
  /** Practices required before the phase can complete (null = no gate) */
  target: number | null;
  /** habit_check logs recorded on this phase for this habit */
  checks: number;
}

/** A phase's BECOME side resolved to a REAL UserTrait in the identity space
 *  (linked via UserTrait.pwfIds at enrollment; name-matched for legacy data) */
export interface QuestLinkedTrait {
  id: string;
  name: string;
  emoji: string | null;
  category: "strength" | "value" | "aspiration" | "skill";
  /** 0 Aspiration · 1 Emerging · 2 Developing · 3 Embodied */
  level: number;
  /** True when this trait IS the phase identity (auto-raised to Emerging on completion) */
  isIdentity: boolean;
}

export interface QuestPhase {
  id: string;
  title: string;
  state: LifecycleState;
  completedAt: string | null;
  /** The phase's own runnable activity (hub link / manifestation / linkedActivity) */
  linkedPrescriptionId: string | null;
  /** Every runnable activity of the phase (own link + linked activity items), named */
  linkedActivities: Array<{ prescriptionId: string; name: string | null }>;
  do: string;
  identity: string;
  traits: string[];
  linkedTraits: QuestLinkedTrait[];
  habits: QuestPhaseHabit[];
  hasCheckIn: boolean;
  /** Reflections required before completing (null = optional) */
  checkInTarget: number | null;
  /** Distinct days on which the learner reflected */
  checkInDays: number;
  /** Inner-journey engagement counts — never the check-in text itself */
  glow: { habitChecks: number; checkIns: number };
  items: QuestItem[];
}

export interface QuestTraitAssessment {
  level: number;
  note: string | null;
  date: string;
}

export interface QuestTraitEvidence {
  type: "insight" | "engagement" | "reflection" | "action_item" | "note";
  relationship: "develops" | "demonstrates" | "challenges";
  note: string;
  date: string | null;
}

export interface QuestIdentityTrait {
  id: string;
  name: string;
  emoji: string | null;
  category: "strength" | "value" | "aspiration" | "skill";
  /** 0 Aspiration · 1 Emerging · 2 Developing · 3 Embodied */
  level: number;
  /** Real progression receipts: engagement/reflection evidence entries */
  evidenceCount: number;
  lastAssessedAt: string | null;
  /** Full assessment history, oldest first — the growth line */
  history: QuestTraitAssessment[];
  /** Latest evidence entries, newest first */
  recentEvidence: QuestTraitEvidence[];
}

export interface QuestIdentity {
  spaceId: string;
  name: string;
  identityLabel: string | null;
  vision: string | null;
  color: string | null;
  icon: string | null;
  traits: QuestIdentityTrait[];
}

export interface QuestStreak {
  current: number;
  longest: number;
  totalDays: number;
  lastCheckDate: string | null;
}

export interface QuestSummary {
  journey: {
    id: string;
    title: string;
    summary: string;
    become: { identity?: string; traits?: string[] };
    state: LifecycleState;
    completedAt: string | null;
    isTemplate: boolean;
  };
  phases: QuestPhase[];
  streak: QuestStreak;
  /** Habit names already checked on the learner's local "today" */
  todayChecks: string[];
  identity: QuestIdentity | null;
  theme: QuestTheme;
}

export const getQuestSummary = async (
  retryFetch: Function,
  journeyId: string
): Promise<QuestSummary> => {
  // Same date key the walk stamps on habit_check logs (JourneyWalk todayKey),
  // so "checked today" always agrees between the two surfaces
  const today = new Date().toISOString().slice(0, 10);
  const res = await retryFetch(
    `${config.API_BASE}/api/pwf/${journeyId}/quest-summary?today=${today}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to load journey map: ${res.statusText}`);
  }
  return res.json();
};

/** Learner describes a theme in words; the AI designs and applies it. */
export const generateQuestTheme = async (
  retryFetch: Function,
  journeyId: string,
  description: string
): Promise<{ success: boolean; custom: CustomThemeSpec }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/pwf/${journeyId}/quest-theme/generate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ description }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to generate theme: ${res.statusText}`);
  }
  return res.json();
};

export const setQuestTheme = async (
  retryFetch: Function,
  journeyId: string,
  body: { pack: string; palette?: QuestThemePalette } | { clear: true }
): Promise<{ success: boolean; themeOverride: { pack: string } | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${journeyId}/quest-theme`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to set theme: ${res.statusText}`);
  }
  return res.json();
};

// ── Mentor: fetch mentee processes for a specific activity ────────────────────

export const getMenteeProcessesForActivity = async (
  retryFetch: Function,
  prescriptionId: string,
  studentId: string,
): Promise<any[]> => {
  const qs = new URLSearchParams({
    fiPrescription: prescriptionId,
    fiInitiator: studentId,
    limit: "20",
  });
  const res = await retryFetch(`${config.API_BASE}/api/processes?${qs}`, {
    credentials: "include",
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.processes ?? data.data ?? [];
};

export const syncPwfFromTemplate = async (
  retryFetch: Function,
  pwfId: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/sync-from-template`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Sync failed: ${res.statusText}`);
  }
  return res.json();
};

// ── Template Sharing ──────────────────────────────────────────────────────────

export const listMentoringPwfs = async (retryFetch: Function): Promise<Pwf[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/mentoring`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list mentoring PWFs: ${res.statusText}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

export interface MentorCollection {
  _id: string;
  name: string;
  kind?: string;
  level?: number;
}

export const listMentorCollections = async (retryFetch: Function): Promise<MentorCollection[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/collections`, {
    credentials: "include",
  });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : data.collections ?? data.list ?? [];
};

export interface PwfProgressStudent {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
  pwfInstanceId: string | null;
  title: string | null;
  lifecycleState: string;
  lastActivityAt: string | null;
  stalled: boolean;
}

export interface PwfProgressReport {
  collectionId: string;
  collectionName: string;
  templateId: string;
  students: PwfProgressStudent[];
  totalStudents: number;
}

export interface PwfNodeProgressStudent {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
  instanceNodeId: string | null;
  title: string | null;
  lifecycleState: string;
  lastActivityAt: string | null;
  isPrivacyOptedOut: boolean;
  stalled: boolean;
}

export interface PwfNodeProgressReport {
  collectionId: string;
  collectionName: string;
  templateNodeId: string;
  students: PwfNodeProgressStudent[];
  totalStudents: number;
}

export const getPwfNodeProgress = async (
  retryFetch: Function,
  collectionId: string,
  templateNodeId: string
): Promise<PwfNodeProgressReport> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/collection/${collectionId}/report/pwf-node-progress?templateNodeId=${encodeURIComponent(templateNodeId)}`,
    { credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to load node progress: ${res.statusText}`);
  return res.json();
};

export const getPwfProgressForCollection = async (
  retryFetch: Function,
  collectionId: string,
  templateId: string
): Promise<PwfProgressReport> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/collection/${collectionId}/report/pwf-progress?templateId=${encodeURIComponent(templateId)}`,
    { credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to load PWF progress: ${res.statusText}`);
  return res.json();
};

export const listSharedPwfs = async (retryFetch: Function): Promise<Pwf[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/shared`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list shared blueprints: ${res.statusText}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

export const sharePwf = async (
  retryFetch: Function,
  pwfId: string,
  body: { userId?: string; collectionId?: string }
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/share`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to share blueprint: ${res.statusText}`);
  }
  return res.json();
};

export const unsharePwf = async (
  retryFetch: Function,
  pwfId: string,
  body: { userId?: string; collectionId?: string }
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/unshare`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to unshare blueprint: ${res.statusText}`);
  }
  return res.json();
};

export const addCoOwner = async (
  retryFetch: Function,
  pwfId: string,
  userId: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/add-co-owner`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to add co-owner: ${res.statusText}`);
  }
  return res.json();
};

export const removeCoOwner = async (
  retryFetch: Function,
  pwfId: string,
  userId: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/remove-co-owner`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to remove co-owner: ${res.statusText}`);
  }
  return res.json();
};

/**
 * Journey enrollment (Studio) — instantiate + create the learner's identity
 * space from the journey's BECOME side (phase identities become aspiration
 * traits). On 409 the thrown error carries `existingInstanceId`.
 */
export const enrollInJourney = async (
  retryFetch: Function,
  templateId: string
): Promise<{ pwf: Pwf; identitySpaceId: string | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${templateId}/enroll`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed to start journey: ${res.statusText}`);
    (e as any).status = res.status;
    (e as any).existingInstanceId = err.existingInstanceId;
    throw e;
  }
  return res.json();
};

export const instantiatePwf = async (
  retryFetch: Function,
  pwfId: string
): Promise<Pwf> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/instantiate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to instantiate blueprint: ${res.statusText}`);
  }
  return res.json();
};

// ── Collaboration ─────────────────────────────────────────────────────────────

export const inviteCollaborator = async (
  retryFetch: Function,
  pwfId: string,
  email: string
): Promise<{ invited?: boolean; joined?: boolean; alreadyPending?: boolean; alreadyCollaborator?: boolean; email?: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to send invitation: ${res.statusText}`);
  }
  return res.json();
};

export const acceptPwfInvite = async (
  retryFetch: Function,
  token: string
): Promise<{ ok: boolean; pwfId: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/invite/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to accept invitation: ${res.statusText}`);
  }
  return res.json();
};

export const removeCollaborator = async (
  retryFetch: Function,
  pwfId: string,
  userId: string
): Promise<void> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/${pwfId}/collaborators/${userId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Failed to remove collaborator: ${res.statusText}`);
  }
};

// ── Focus Areas ───────────────────────────────────────────────────────────────

// How a person plans to close a gap/focus area — generic across every
// feature that identifies one (auto-ingenuity dimensions, Reality Shaping
// target measures, and any future gap source), never a per-feature concept.
// "develop" is the only strategy with a further sub-choice (what kind of
// thing addresses it); mentorship/delegate are leaf choices on their own.
// See planning/design-principles.md.
export type ResolutionStrategy = "develop" | "mentorship" | "delegate";

export const RESOLUTION_STRATEGY_LABELS: Record<ResolutionStrategy, string> = {
  develop: "Develop it myself",
  mentorship: "Get mentorship",
  delegate: "Delegate to someone",
};

// What kind of thing the person will use to develop this — only meaningful
// when resolutionStrategy === "develop". Every option is label-only for now
// (recorded as a choice + optional note, no real linking/creation yet) —
// each becomes a real link one at a time as a future, separate step.
export type DevelopArtifactType = "pwf" | "action_item" | "activity" | "program" | "identity_space";

export const DEVELOP_ARTIFACT_TYPE_LABELS: Record<DevelopArtifactType, string> = {
  pwf: "Link an existing PWF",
  action_item: "Create an action item",
  activity: "Link or create an activity",
  program: "Link or create a program",
  identity_space: "Link an identity space",
};

// One entry per gap/dimension a person has marked or focused on — the single
// place a gap's status AND resolution live, replacing the old separate
// activeGaps/notedGaps/gapReasons/gapActions keys. Keyed by dim.slug for
// auto-ingenuity-style dimensions, or "tm:<targetMeasure.key>" for Reality
// Shaping-style target measures (see focusKeyForTargetMeasure) — the prefix
// keeps the two id spaces collision-safe without a second parallel map.
// Deliberately the same shape and the same UI regardless of what identified
// the gap — no per-feature resolution taxonomy; see planning/design-principles.md.
export interface GapEntry {
  status: "focused" | "marked";
  reason: string;
  updatedAt: string;
  resolutionStrategy?: ResolutionStrategy;
  // Only set when resolutionStrategy === "develop".
  developArtifactType?: DevelopArtifactType;
  // Only set when developArtifactType === "pwf" and a target PWF has been
  // linked. Cached name mirrors Program.MilestoneSchema's linkedEntityId/
  // linkedEntityName pattern — avoids a fetch just to render "linked to: X".
  // The target PWF also gets a back-reference in its own
  // pwfMeta.linkedObjects (see linkGapResolutionPwf) — a two-sided link.
  linkedEntityId?: string;
  linkedEntityName?: string;
  // Frozen at first creation only, never overwritten by later status/
  // resolution changes — the actual numbers that justified flagging this
  // gap in the first place (self-assessed level vs. required level).
  // Without this, returning to a focus item later shows only whatever the
  // CURRENT latest sitting happens to say, and the original analytical work
  // that produced the gap is unrecoverable once a new sitting is submitted.
  snapshotSelfAssessed?: number;
  snapshotRequired?: number;
}

export interface FocusAreaEntry {
  pwfId: string;
  pwfTitle: string;
  defaultLanguage: string;
  dimensionSetId: string | null;
  dimensionSetName: string | null;
  dimensions: Array<{ slug: string; name: string; description: string }>;
  subRatingFields: Array<{ key: string; label: string; scaleType: string }>;
  scale: { min: number; max: number };
  // Reality Shaping's target measures — resolves the label for gapEntries
  // keys prefixed "tm:<key>" (see focusKeyForTargetMeasure), the same way
  // `dimensions` resolves plain dim.slug keys.
  targetMeasures: Array<{ key: string; label: string }>;
  gapEntries: Record<string, GapEntry>;
  fullConfig: Record<string, any>;
}

export const listFocusAreas = async (
  retryFetch: Function
): Promise<FocusAreaEntry[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/pwf/focus-areas`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch focus areas");
  return res.json();
};
